/**
 * Fiche JOUEUR au survol (ou tap / focus) d'une pastille du mini-terrain. Un seul
 * contrôleur global écoute les éléments `[data-player]` ; il lit l'id + le nom
 * (data-name), récupère la fiche (playerData, en cache) et l'affiche dans une
 * bulle fixe `pointer-events:none`, placée à côté de la pastille.
 */
import { fetchPlayer, fetchPlayerForm, preloadPlayers, type PlayerInfo, type PlayerForm } from "../app/playerData.ts";
import { esc } from "./html.ts";
import { t } from "../app/i18n.ts";

const rateCls = (r: number): string => (r >= 7.5 ? "hi" : r >= 6.5 ? "mid" : "lo");

let tip: HTMLElement | null = null;
let installed = false;
let activeEl: HTMLElement | null = null;
let token = 0; // anti-course : invalide les fetchs dont le survol a changé

function ensureTip(): HTMLElement {
  if (!tip) {
    tip = document.createElement("div");
    tip.className = "pt-tip hide";
    document.body.appendChild(tip);
  }
  return tip;
}

function place(anchor: HTMLElement, el: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  el.style.left = "0px";
  el.style.top = "0px";
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  let left = r.left + r.width / 2 - w / 2;
  left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
  let top = r.top - h - 10; // au-dessus de la pastille…
  if (top < 8) top = r.bottom + 10; // …sinon en dessous
  top = Math.max(8, Math.min(top, window.innerHeight - h - 8));
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

const POS_KEY: Record<string, string> = {
  Goalkeeper: "fiche.player.pos.goalkeeper",
  Defender: "fiche.player.pos.defender",
  Midfielder: "fiche.player.pos.midfielder",
  Attacker: "fiche.player.pos.attacker",
};

function card(name: string, info: PlayerInfo | null, form: PlayerForm | null): string {
  const photo = info?.photo
    ? `<img class="pt-photo" src="${esc(info.photo)}" alt="" loading="lazy" referrerpolicy="no-referrer">`
    : `<div class="pt-photo ph"><i class="ti ti-user" aria-hidden="true"></i></div>`;
  const pos = info?.pos ? (POS_KEY[info.pos] ? t(POS_KEY[info.pos]!) : info.pos) : null;
  const sub = info
    ? [pos, info.age != null ? t("fiche.player.age", { age: info.age }) : null, info.nationality].filter(Boolean).join(" · ")
    : "";
  // Note du DERNIER match, mise en avant (la « forme » la plus récente).
  const last =
    form && form.rating != null
      ? `<div class="pt-last"><span class="pt-last-l">${t("fiche.player.lastMatch")}</span><span class="pt-rate ${rateCls(form.rating)}">${form.rating}</span>${form.oppCode ? `<span class="pt-vs">${t("fiche.player.vsOpp", { opp: esc(form.oppCode) })}</span>` : ""}${form.goals ? `<span class="pt-g">⚽${form.goals}</span>` : ""}</div>`
      : "";
  let body: string;
  if (!info) {
    body = last ? "" : `<div class="pt-empty">${t("fiche.player.unavailable")}</div>`;
  } else if (info.apps || info.goals || info.assists || info.rating != null) {
    body = `<div class="pt-stats">
      <span><b>${info.apps}</b> matchs</span>
      <span><b>${info.goals}</b> buts</span>
      <span><b>${info.assists}</b> passes</span>
      ${info.rating != null ? `<span class="pt-noteline"><span class="pt-rate ${rateCls(info.rating)}">${info.rating}</span> moy. saison</span>` : ""}
    </div>`;
  } else {
    body = `<div class="pt-empty">Pas de stats cette saison</div>`;
  }
  return `${photo}<div class="pt-info"><div class="pt-name">${esc(info?.name ?? name)}</div>${sub ? `<div class="pt-sub">${esc(sub)}</div>` : ""}${last}${body}</div>`;
}

async function show(el: HTMLElement): Promise<void> {
  if (el === activeEl && tip && !tip.classList.contains("hide")) return;
  activeEl = el;
  const id = Number(el.dataset.player);
  const name = el.dataset.name ?? "Joueur";
  if (!Number.isInteger(id)) return;
  const mine = ++token;
  const t = ensureTip();
  const [info, form] = await Promise.all([fetchPlayer(id), fetchPlayerForm(id)]);
  if (mine !== token) return; // un autre survol a pris la main
  t.innerHTML = card(name, info, form);
  t.classList.remove("hide");
  place(el, t);
}

function hide(): void {
  activeEl = null;
  token++;
  tip?.classList.add("hide");
}

/** Masque l'aperçu (appelé quand la fiche joueur complète s'ouvre au clic). */
export function hidePlayerTip(): void {
  hide();
}

const playerEl = (e: Event): HTMLElement | null =>
  e.target instanceof Element ? e.target.closest<HTMLElement>("[data-player]") : null;

/** Installe le contrôleur (idempotent) et précharge les fiches. */
export function registerPlayerStats(): void {
  if (installed) return;
  installed = true;
  preloadPlayers();
  document.addEventListener("mouseover", (e) => {
    const el = playerEl(e);
    if (el) void show(el);
  });
  document.addEventListener("mouseout", (e) => {
    const el = playerEl(e);
    if (!el) return;
    const rel = e instanceof MouseEvent ? e.relatedTarget : null;
    if (rel instanceof Element && (el.contains(rel) || rel.closest("[data-player]"))) return;
    hide();
  });
  document.addEventListener("focusin", (e) => {
    const el = playerEl(e);
    if (el) void show(el);
  });
  // Le survol/focus donne l'aperçu rapide. Le CLIC/TAP est géré par playerProfile
  // (ouvre la fiche complète). On garde UNE responsabilité ici : un tap HORS d'un
  // joueur referme l'aperçu — sur mobile, mouseout ne suffit pas (sinon une bulle
  // peut rester collée). Un tap SUR un joueur ouvre la fiche, qui masque l'aperçu.
  document.addEventListener("click", (e) => {
    if (!playerEl(e)) hide();
  });
}
